// /scada/shift-handover — electronic operational log / shift handover (R3).
// Auto-collects the shift's events, commands and unresolved items, plus a
// handover form with signatures. Replaces the paper station log. Mock data.
import { useMemo, useState } from 'react';
import ScadaLayout from '@/components/feature/ScadaLayout';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { mockEvents, mockSkoCommands } from '@/scada/mock/events';

const fmt = (s: string) => new Date(s).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export default function ScadaShiftHandoverPage() {
  const events = useMemo(() => mockEvents(), []);
  const cmds = useMemo(() => mockSkoCommands(), []);
  const [accept, setAccept] = useState('');
  const [notes, setNotes] = useState('');
  const [done, setDone] = useState(false);

  const unresolved = events.filter((e) => e.status === 'active');
  const shiftItems = [
    ...cmds.map((c) => ({ ts: c.receivedAt, kind: 'Команда', text: `${c.stationName}: ${c.kind === 'op_safety' ? 'опер. безпека' : 'СКО'} ${c.limitPct}% (${c.status === 'completed' ? 'завершено' : 'виконується'})` })),
    ...events.map((e) => ({ ts: e.ts, kind: e.type === 'manual' ? 'Ручна дія' : e.severity === 'alarm' ? 'Аварія' : e.severity === 'warning' ? 'Попередження' : 'Подія', text: `${e.stationName}${e.device ? ' · ' + e.device : ''}: ${e.message}` })),
  ].sort((a, b) => +new Date(b.ts) - +new Date(a.ts));

  const inp = { background: scada.bg, border: `1px solid ${scada.border}`, color: scada.text, borderRadius: 6, padding: '8px 10px', fontSize: 13, width: '100%' } as const;

  return (
    <ScadaLayout active="shift">
      {/* Shift header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" style={{ marginBottom: 16 }}>
        {[
          { k: 'Зміна', v: 'Денна (08:00–20:00)' },
          { k: 'Дата', v: new Date().toLocaleDateString('uk-UA') },
          { k: 'Диспетчер', v: 'Західенко В.' },
          { k: 'Невирішених питань', v: String(unresolved.length) },
        ].map((x) => (
          <div key={x.k} style={{ background: scada.panel2, border: `1px solid ${scada.border}`, borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ color: scada.textMuted, fontSize: 11 }}>{x.k}</div>
            <div style={{ color: scada.text, fontSize: 15, fontWeight: 600, marginTop: 2 }}>{x.v}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Auto shift log */}
        <div className="md:col-span-2">
          <SectionCard title="Події за зміну" subtitle="Автоматично зібрано: команди, аварії, ручні дії">
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {shiftItems.map((it, i) => (
                <div key={i} className="flex items-start gap-3" style={{ padding: '7px 0', borderBottom: `1px solid ${scada.border}` }}>
                  <span style={{ color: scada.textMuted, fontSize: 11, width: 92, flexShrink: 0 }}>{fmt(it.ts)}</span>
                  <span style={{ color: scada.textDim, fontSize: 11, width: 96, flexShrink: 0 }}>{it.kind}</span>
                  <span style={{ color: scada.text, fontSize: 12 }}>{it.text}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* Handover form */}
        <div className="flex flex-col gap-4">
          <SectionCard title="Невирішені питання">
            {unresolved.length === 0 ? <span style={{ color: scada.textMuted, fontSize: 12 }}>Немає.</span> :
              unresolved.map((e) => (
                <div key={e.id} className="flex items-center gap-2" style={{ padding: '5px 0' }}>
                  <span style={{ width: 7, height: 7, borderRadius: 999, background: e.severity === 'alarm' ? scada.state.alarm : scada.state.warning, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ color: scada.textDim, fontSize: 12 }}>{e.stationName}: {e.message}</span>
                </div>
              ))}
          </SectionCard>

          <SectionCard title="Передати зміну">
            {done ? (
              <div style={{ color: scada.state.ok, fontSize: 13 }}>
                <i className="ri-check-double-line" /> Зміну передано {accept ? `(${accept})` : ''}. Запис збережено в журнал.
              </div>
            ) : (
              <>
                <label style={{ color: scada.textMuted, fontSize: 11, display: 'block', marginBottom: 3 }}>Приймає зміну (ПІБ)</label>
                <input value={accept} onChange={(e) => setAccept(e.target.value)} placeholder="напр. Кравченко О." style={{ ...inp, marginBottom: 10 }} />
                <label style={{ color: scada.textMuted, fontSize: 11, display: 'block', marginBottom: 3 }}>Примітки до передачі</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="на що звернути увагу наступній зміні…" style={{ ...inp, resize: 'vertical', marginBottom: 10 }} />
                <button onClick={() => setDone(true)} disabled={!accept.trim()}
                  style={{ background: accept.trim() ? scada.accent : scada.panel2, color: accept.trim() ? '#fff' : scada.textMuted, border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: accept.trim() ? 'pointer' : 'not-allowed', width: '100%' }}>
                  Підписати і передати зміну
                </button>
              </>
            )}
          </SectionCard>
        </div>
      </div>
    </ScadaLayout>
  );
}
